/**
 * -----------------------------------------------------------------------------
 * Popup - Create New Geozone
 * -----------------------------------------------------------------------------
 * This popup is used for creating a new geozone in the organization.
 */

import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useLoggedInUserData } from "../../../utils/user";
import { TModalsState, setModalsData, setMapStateData } from "../../../api/store/commonSlice";
import { Formik, useFormik } from "formik";
import { TFormFieldNames, YupValidationSchema, formInitialValues } from "./validation";
import { AdminFormFieldDropdown, AdminFormFieldInput, AdminFormFieldSubmit } from "../../../components/admin/formFields";
import { useCreateOrganizationGeozoneMutation } from "../../../api/network/adminApiServices";
import { toast } from "react-toastify";
import { routeUrls } from "../../../navigation/routeUrls";
import { serializeErrorKeyValues } from "../../../api/network/errorCodes";
import { geozonePrepareMapState } from "../../admin-details/geozone/util";
import { TMapState } from "../../../types/map";
import { GeozoneType, LegacyGeozoneType } from "../../../api/types/Geozone";
import { APP_CONFIG } from "../../../constants/constants";
import { ZONE_TYPES_OPTIONS } from "../../admin-details/geozone/constants";
import FTModal from "../../../components/common/FTModal";
import { useMemo } from "react";
import FormActionButtons from "../../../components/admin/formActionButtons";

const AdminsGeozonesCreateNew = () => {
  const { t: tMain } = useTranslation();
  const { t } = useTranslation('translation', { keyPrefix: 'admins.geozones.create_new' });
  const modalsState = useSelector((state: { commonReducer: { modals: TModalsState } }) => state.commonReducer.modals);
  const mapState = useSelector((state: { commonReducer: { mapState: TMapState } }) => state.commonReducer.mapState);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const thisUserOrganizationId = useLoggedInUserData("ownerOrganizationId");

  // geozone mutation
  const [createOrgGeozoneAPITrigger] = useCreateOrganizationGeozoneMutation();

  const hideModal = () => {
    dispatch(setModalsData({ ...modalsState, showCreateGeozone: false }));
  };

      const formik = useFormik({
        initialValues:formInitialValues,
        validationSchema:YupValidationSchema,
        onSubmit:(values, { setSubmitting }) => {
          // clear previous mapState
          if (APP_CONFIG.DEBUG.GEOZONES) console.log('[D] DISPATCH TO CLEAR MAP STATE DATA BEFORE NAVIGATING TO NEW GEOZONE PAGE');
          dispatch(setMapStateData(
            geozonePrepareMapState({
              seedMapState: mapState,
              shapeType: ZONE_TYPES_OPTIONS[2].value as any as LegacyGeozoneType, // TODO: ambiguous conversion, can be fixed
              seedMapData: undefined,
              locs: [],
            })
          ));
          // api call
          createOrgGeozoneAPITrigger({
            accountid: `${thisUserOrganizationId}`,
            geozoneid: values.id,
            zonetype: values.type as LegacyGeozoneType.CIRCLE | LegacyGeozoneType.ROUTE | LegacyGeozoneType.POLYGON,
            sortid: 0,
            latitude1: APP_CONFIG.MAPS.DEFAULTS.CENTER.latitude,
            longitude1: APP_CONFIG.MAPS.DEFAULTS.CENTER.longitude,
            radius: APP_CONFIG.MAPS.DEFAULTS.RADIUS,
          })
            .unwrap()
            .then((data) => {
              if (!!data?.geozoneid) {
                dispatch(setModalsData({ ...modalsState, showCreateGeozone: false }));
                toast.success(t('toast.create_success'), { autoClose: 10000 });
                navigate(`${routeUrls.dashboardChildren.adminChildren.geozones}/${data?.geozoneid}`, { state: { new: true } });
              } else {
                toast.error(t('toast.create_failed'));
              }
            })
            .catch((error) => {
              const errors = !!error?.data ? serializeErrorKeyValues(error?.data) : [t('toast.create_failed')];
              // if getting duplicate/Duplicate string in error message then show the message as "Zone ID already exists"
              if (errors[0].includes('Duplicate')) {
                toast.error(t('toast.create_duplicate_failed'));
              } else {
                toast.error(errors?.join(' '));
              }
            })
            .finally(() => {
              setSubmitting(false);
            });
        }
      });
      const{
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit,
        isSubmitting,
        dirty,
        setFieldValue,
        setTouched
      } = formik;
        const invalidFields =
          Object.keys(errors)
            .filter(key => !!errors[key as TFormFieldNames]);
        const isFormValid = invalidFields.length === 0 && dirty;
        const MemoizedFormActionButtons = useMemo(() => {
          return (
            <>
              <FormActionButtons
                buttons={[
                  {
                    label:t('submit_button'),
                    variant:"primary",
                    disabled:!isFormValid || isSubmitting /* || isLoading*/
                  },
                  {
                    label:tMain('cancel'),
                    type:"button",
                    variant:"danger-transparent",
                    disabled:isSubmitting, /* || isLoading*/
                    onClick:()=>hideModal()
                  },
                ]}
              />
            </>
          );
        }, [
          isFormValid,
          isSubmitting,        
        ]);
      if (modalsState.showCreateGeozone === false) return null; 
  return (
    <FTModal
      visible={modalsState.showCreateGeozone}
      heading={t("heading")}
      onHide={hideModal}
      disableOverlay={true}
    >
            <form className="grid grid-cols-6 gap-6" onSubmit={handleSubmit}>
              <p className="col-span-6 mt-0 leading-relaxed text-gray-500">
                {t("sub_heading")}
              </p>

              <AdminFormFieldInput
                label={t("id")}
                type="text"
                id="Id"
                name="id"
                placeholder={t("id_placeholder")}
                value={values.id}
                onChange={handleChange}
                onBlur={handleBlur}
                error={errors.id}
                touched={touched.id}
              />

              <AdminFormFieldDropdown
                label={t("type")}
                id="type"
                name="type"
                value={`${values.type}`}
                options={ZONE_TYPES_OPTIONS}
                onChange={(e) => setFieldValue("type", e?.value)}
                onBlur={(e) => { setTouched({ type: true }); handleBlur(e) }}
                error={errors.type}
                touched={touched.type}
              />
             {MemoizedFormActionButtons}
              {/* <AdminFormFieldSubmit
                label={t('submit_button')}
                variant="primary"
                disabled={!isFormValid || isSubmitting} // || isLoading
              />

              <AdminFormFieldSubmit
                label={tMain('cancel')}
                type="button"
                variant="danger-transparent"
                disabled={isSubmitting }// || isLoading
                onClick={hideModal}
              /> */}
            </form>
    </FTModal>
  )
}

export default AdminsGeozonesCreateNew;